import { useAuth } from '@/core/contexts/AuthContext';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { preloadAmenityCatalog } from '@/core/services/amenityCatalogService';
import { Redirect } from 'expo-router';
import React, { useEffect } from 'react';
import { Platform } from 'react-native';

export interface GuestRouteGateProps {
  children: React.ReactNode;
}

/**
 * Public guest catalogue — web only, logged-out visitors only.
 * Flat routes (`/guest`, `/guest-details`) so static export is `.html` files,
 * not a `guest/` directory (avoids nginx trailing-slash :8080 redirects).
 */
export function GuestRouteGate({ children }: Readonly<GuestRouteGateProps>) {
  const { isAuthenticated, isLoading } = useAuth();
  const { currentLanguage, isLoading: isI18nLoading } = useI18nContext();
  const isGuestVisitor = Platform.OS === 'web' && !isLoading && !isAuthenticated;

  // Shared by every guest page; the catalog is cached per locale.
  useEffect(() => {
    if (isGuestVisitor && !isI18nLoading) {
      void preloadAmenityCatalog(currentLanguage);
    }
  }, [isGuestVisitor, isI18nLoading, currentLanguage]);

  if (Platform.OS !== 'web') {
    return <Redirect href="/welcome" />;
  }

  if (isLoading) {
    return null;
  }

  if (isAuthenticated) {
    return <Redirect href="/(auth)/(home)" />;
  }

  return <>{children}</>;
}
